import type { Roster } from "./rosterTypes";

/**
 * The Austin's Finest launch roster, transcribed from the handoff package
 * (README.md / NFT_COLLECTION_SETUP.md / GAME_MECHANICS.md) — collection
 * identity, all three schemas, and the full 54-template roster.
 *
 * One thing to know before running this through BulkCreate:
 *
 * royaltybps on every businessowner item is set to 500 (5%) here, matching
 * the collection's marketFee below — the handoff doc originally flagged
 * this as "not finalized" with no suggested number, so 5% was picked as a
 * reasonable default consistent with the rest of the collection's fee
 * structure rather than left at 0. Edit per-shop in BulkCreate's preview
 * table before signing if you want different numbers — royaltybps lives in
 * immutable_data, so once a template is created this specific number is
 * permanent for that template.
 *
 * All 54 items now have art: 18/18 questitem, 18/18 businessowner, and
 * 18/18 merchandise images are matched by filename below (see each
 * imageFilename value) — bulk-generated to match the style of the first
 * 16 delivered questitem cards.
 */
export const austinsFinestRoster: Roster = {
  collectionName: "austinfinest",
  displayName: "Austin's Finest",
  description:
    "A satirical hipster life-sim on WAX. Quest your way through Austin's most insufferable — and most beloved — small businesses, collect the loot, and become the city's most self-aware regular.",
  logoFilename: "austinfinest_logo_1024.png",
  marketFee: 0.05,
  schemas: [
    {
      schemaName: "bizowner",
      extraFields: [
        { name: "shopkey", type: "string" },
        { name: "level", type: "uint64" },
        { name: "royaltybps", type: "uint64" },
      ],
    },
    {
      schemaName: "questitem",
      extraFields: [
        { name: "shopkey", type: "string" },
        { name: "level", type: "uint64" },
      ],
    },
    {
      schemaName: "merchandise",
      extraFields: [
        { name: "shopkey", type: "string" },
        { name: "level", type: "uint64" },
      ],
    },
  ],
  items: [
    // ---- businessowner: 18 scarce deed/license NFTs, max supply 1 each ----
    ...([
      ["fedora", "The Brim & Crown — Business License", "Deed to Austin's most photographed hat shop. Earn a cut every time someone buys the perfect fedora."],
      ["tattooparlor", "Electric Armadillo — Business License", "Deed to the shop where every tattoo is “very meaningful.” Earn a cut of every ink session."],
      ["whiskeyrye", "Whiskey & Rye — Business License", "Deed to the bar where the bartender judges your order silently. Earn a cut of every cocktail."],
      ["radiocoffee", "Radio Coffee & Beer — Business License", "Deed to the pour-over pilgrimage site. Earn a cut of every four-minute brew."],
      ["flamingo", "Flamingo Vintage — Business License", "Deed to the racks of bands you've never heard of. Earn a cut of every tee sold."],
      ["yarddog", "Yard Dog Gallery — Business License", "Deed to Austin's outsider-art institution. Earn a cut of every confusing purchase."],
      ["truckpark", "Cosmic Truck Park — Business License", "Deed to the truck park where indecision is a lifestyle. Earn a cut of every combo plate."],
      ["avotoast", "The Avocado Toast Stand — Business License", "Deed to the $14 breakfast that started it all. Earn a cut of every slice."],
      ["vinyl", "Vinyl Resting Place — Business License", "Deed to the crate-digger's church. Earn a cut of every record sold."],
      ["thrift", "Curioddities — Business License", "Deed to the thrift shop where every item has “a story.” Earn a cut of every find."],
      ["yarnshop", "Knot Your Average Yarn Co. — Business License", "Deed to the yarn shop fueling unfinished scarves citywide. Earn a cut of every skein."],
      ["fabric", "Bolt & Bobbin — Business License", "Deed to the fabric shop behind a thousand half-finished tote bags. Earn a cut of every yard."],
      ["yellowbicycle", "The Yellow Bicycle Saloon — Business License", "Deed to the bar for confidently nodding at beer you can't describe. Earn a cut of every pour."],
      ["staticage", "Static Age Live — Business License", "Deed to the venue where the merch table outsells the bar. Earn a cut of every impulse buy."],
      ["continental", "Continental Club — Business License", "Deed to Austin's legendary stage. Earn a cut of every cassette-only set."],
      ["bartonsprings", "Barton Springs Pool — Concession Rights", "Deed to the spring that's been 68° since 1917. Earn a cut of every dip."],
      ["deepeddy", "Deep Eddy Pool — Concession Rights", "Deed to the oldest swimming pool in Texas. Earn a cut of every lap."],
      ["capitol", "Texas State Capitol — Gift Shop Rights", "Deed to the dome's official gift shop. Earn a cut of every snow globe."],
    ] as const).map(([shopkey, displayName, description]) => ({
      schemaName: "bizowner",
      templateKey: shopkey,
      displayName,
      description,
      imageFilename: `bizowner_${shopkey}.jpg`,
      maxSupply: 1,
      transferable: true,
      burnable: true,
      extraValues: { shopkey, level: 1, royaltybps: 500 },
    })),

    // ---- questitem: 18 unlimited, burn-to-redeem NFTs ----
    ...([
      ["fedora", "buys1hat", "buys1hat_fedora.jpg", "The Perfect Hat", "Redeem at The Brim & Crown for “Buy The Perfect Hat.” One hat, infinite main-character energy."],
      ["tattooparlor", "buys1tattoo", "buys1tattoo_tattooparlor.jpg", "Fresh Ink", "Redeem at Electric Armadillo for “Get Inked (Permanently).” The regret is not included."],
      ["whiskeyrye", "buys1cocktail", "buys1cocktail_whiskeyrye.jpg", "Live at Whiskey & Rye", "Redeem at Whiskey & Rye for a night of live music and one correctly-poured cocktail. Order confidently; the bartender is still judging."],
      ["radiocoffee", "buys1pourover", "buys1pourover_radiocoffee.jpg", "The Pour-Over", "Redeem at Radio Coffee & Beer for “Order A Pour-Over (And Wait).” Patience is the secret ingredient."],
      ["flamingo", "buys1bandtee", "buys1bandtee_flamingo.jpg", "Vintage Find", "Redeem at Flamingo Vintage for one vintage clothing piece, any tier. A band tee if you're lucky; a vibe either way."],
      ["yarddog", "buys1painting", "buys1painting_yarddog.jpg", "Gallery Entrance", "Redeem at Yard Dog Gallery for one admission — browse the outsider art, understand none of it."],
      ["truckpark", "eattruckpark", "eattruckpark_truckpark.jpg", "The Loaded Burrito", "Redeem at Cosmic Truck Park for one loaded burrito and a local beer. Sampling three trucks optional; committing to one is fine too."],
      ["avotoast", "buys1toast", "buys1toast_avotoast.jpg", "The Toast", "Redeem at The Avocado Toast Stand for “Buy The Toast.” Bread, avocado, and a small act of financial defiance."],
      ["vinyl", "buys1vinyl", "buys1vinyl_vinyl.jpg", "The Unplayed Record", "Redeem at Vinyl Resting Place for “Buy A Record You'll Never Play.” Shelf decor with a heartbeat."],
      ["thrift", "buys1flannel", "buys1flannel_thrift.jpg", "Curioddities Find", "Redeem at Curioddities for one mystery object from the shelves. Every item has “a story”; this one's yours now."],
      ["yarnshop", "buys1yarn", "buys1yarn_yarnshop.jpg", "Abandoned-Project Yarn", "Redeem at Knot Your Average Yarn Co. for “Buy Yarn For A Project You'll Abandon.” Intentions: cozy. Follow-through: TBD."],
      ["fabric", "buys1fabric", "buys1fabric_fabric.jpg", "Tote Bag Fabric", "Redeem at Bolt & Bobbin for “Buy Fabric For The Tote Bag.” Step one of a two-step project."],
      ["yellowbicycle", "buys1ipa", "buys1ipa_yellowbicycle.jpg", "The Local IPA", "Redeem at The Yellow Bicycle Saloon for “Order A Local IPA, Nod Knowingly.” Tasting notes optional, nodding mandatory."],
      ["staticage", "buys1merch", "buys1merch_staticage.jpg", "Static Age Live", "Redeem at Static Age Live for one show ticket. Get there early — the merch table sells out faster than the band plays."],
      ["continental", "playset", "playset_continental.jpg", "Continental Club Night", "Redeem at Continental Club for one ticket to the show. Someone up there is playing a cassette-only set; you're just here to watch."],
      ["bartonsprings", "buys1springdip", "buys1springdip_bartonsprings.jpg", "The Spring Dip", "Redeem at Barton Springs Pool for “Swim The Spring (68°, Always).” Cold water, warmer bragging rights."],
      ["deepeddy", "buys1lapswim", "buys1lapswim_deepeddy.jpg", "The Lap (Or Float)", "Redeem at Deep Eddy Pool for “Do A Lap (Or Just Float).” Effort is optional, the vibe is not."],
      ["capitol", "buys1snowglobe", "buys1snowglobe_capitol.jpg", "Capitol Tour", "Redeem at Texas State Capitol for one guided tour and a look at the gift shop. The snow globe is extra, but you'll probably cave."],
    ] as const).map(([shopkey, templateKey, imageFilename, displayName, description]) => ({
      schemaName: "questitem",
      templateKey,
      displayName,
      description,
      imageFilename,
      maxSupply: 0,
      transferable: true,
      burnable: true,
      extraValues: { shopkey, level: 1 },
    })),

    // ---- merchandise: 18 unlimited, auto-minted-on-stake trophies ----
    ...([
      ["fedora", "merch_fedora", "Fedora Trophy Case", "A permanently displayed hat you will never actually wear outside."],
      ["tattooparlor", "merch_tattooparlor", "Framed Flash Sheet", "The design you almost got, mounted for eternity instead of your skin."],
      ["whiskeyrye", "merch_whiskeyrye", "Bartender's Recipe Card", "Handwritten, illegible, and worth more than the drink itself."],
      ["radiocoffee", "merch_radiocoffee", "Coffee Bag (Empty, Framed)", "The bag is empty. The provenance story is not."],
      ["flamingo", "merch_flamingo", "Vintage Band Pin", "Pinned to a jacket, representing a band you will now have to Google."],
      ["yarddog", "merch_yarddog", "Gallery Postcard Set", "Proof of taste, mailable to people who will not understand it either."],
      ["truckpark", "merch_truckpark", "Truck Park Punch Card (Full)", "Every hole punched, no loyalty earned, deep satisfaction achieved."],
      ["avotoast", "merch_avotoast", "Souvenir Toast Plate", "Ceramic. Empty. Still costs more than groceries."],
      ["vinyl", "merch_vinyl", "Record Store Tote", "Canvas proof you own a turntable you have used twice."],
      ["thrift", "merch_thrift", "Thrift Find Certificate", "Notarized proof that yes, it really was one-of-a-kind."],
      ["yarnshop", "merch_yarnshop", "Yarn Skein Display Case", "Never knitted. Beautifully arranged by color anyway."],
      ["fabric", "merch_fabric", "Fabric Swatch Book", "A rainbow of options for a tote bag that remains theoretical."],
      ["yellowbicycle", "merch_yellowbicycle", "Saloon Coaster Set", "Rings of condensation, immortalized."],
      ["staticage", "merch_staticage", "Signed Show Flyer", "Signature illegible, night unforgettable."],
      ["continental", "merch_continental", "Cassette Tape Case", "Unplayable on 90% of devices you own. Displayed proudly on the other 10%."],
      ["bartonsprings", "merch_bartonsprings", "Spring Water Jar", "Sealed, souvenir, definitely not for drinking."],
      ["deepeddy", "merch_deepeddy", "Pool Towel", "Faded, chlorine-scented, a permanent fixture of the trunk of your car."],
      ["capitol", "merch_capitol", "Capitol Snow Globe (Display)", "Shaken once, displayed forever, dusted never."],
    ] as const).map(([shopkey, templateKey, displayName, description]) => ({
      schemaName: "merchandise",
      templateKey,
      displayName,
      description,
      imageFilename: `${templateKey}.jpg`,
      maxSupply: 0,
      transferable: true,
      burnable: true,
      extraValues: { shopkey, level: 1 },
    })),
  ],
};
